import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, fmtAUD } from "../lib/api";
import { Calculator, FileText, TrendingUp, ArrowRight, ShieldCheck, Plus } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/quotes").then((r) => setQuotes(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const recent = quotes.slice(0, 6);
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = quotes.filter((q) => (q.created_at || "").slice(0, 10) === today).length;
  const totalValue = quotes.reduce((s, q) => {
    const sel = q.selected_scope || "wholesale";
    return s + (q.calculation?.scopes?.[sel]?.sale_price || 0);
  }, 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-64 h-64 bg-gradient-to-bl from-[#fda500]/8 to-transparent rounded-full -translate-y-32 translate-x-32" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">
              {user?.role === "super_admin" ? "Super Administrator" : (user?.role === "admin" ? "Administrator" : (user?.role === "moderator" ? "Moderator Console" : "Staff Console"))}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0A1128]">
              Welcome back, {user?.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-gray-500 mt-1.5">Built for Access. Made with Care.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
          <Link to="/quote-portal/products" className="bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold px-5 py-2.5 rounded-md flex items-center gap-2 transition shadow-md" data-testid="quick-calculator">
              <Plus size={15} /> New Quote
            </Link>
            <Link to="/quote-portal/quotes" className="bg-white border border-gray-300 hover:border-[#254391] text-gray-700 hover:text-[#254391] px-5 py-2.5 rounded-md flex items-center gap-2 transition">
              <FileText size={15} /> View Quotes
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total Quotes" value={loading ? "—" : quotes.length} icon={FileText} colour="blue" testid="stat-total" />
        <Stat label="Today" value={loading ? "—" : todayCount} icon={TrendingUp} colour="orange" testid="stat-today" />
        <Stat label="Total Quoted Value" value={loading ? "—" : fmtAUD(totalValue)} icon={ShieldCheck} colour="blue" mono testid="stat-value" />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/30 to-transparent">
          <div className="text-sm font-bold uppercase tracking-[0.14em] text-[#254391]">Recent Quotes</div>
          <Link to="/quote-portal/quotes" className="text-xs text-[#254391] hover:text-[#fda500] flex items-center gap-1 font-medium">
            View all <ArrowRight size={11} />
          </Link>
        </div>
        {loading ? (
          <div className="p-10 text-sm text-gray-400 text-center">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm text-gray-400 mb-3">No quotes yet</div>
            <Link to="/quote-portal/products" className="inline-flex items-center gap-1 text-[#254391] text-sm hover:text-[#fda500] font-medium">
              <Calculator size={13} /> Create your first quote
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-600 border-b border-gray-200">
                <th className="px-5 py-3 font-semibold">Quote #</th>
                <th className="px-5 py-3 font-semibold">Customer</th>
                <th className="px-5 py-3 font-semibold">Scope</th>
                <th className="px-5 py-3 text-right font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((q) => {
                const sel = q.selected_scope || "wholesale";
                const total = q.calculation?.scopes?.[sel]?.sale_price || 0;
                return (
                  <tr key={q.id} className="border-b border-gray-100 hover:bg-blue-50/30 transition">
                    <td className="px-5 py-3 font-mono text-xs">
                      <Link to={`/quote-portal/quotes/${q.id}`} className="text-[#254391] hover:text-[#fda500]">
                        {q.quote_number}
                      </Link>
                    </td>
                    <td className="px-5 py-3">{q.customer_name}</td>
                    <td className="px-5 py-3">
                      <span className="inline-block bg-[#fff8e7] text-[#0A1128] border border-[#fda500]/40 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded">
                        {sel.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-semibold">{fmtAUD(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, colour = "blue", mono, testid }) {
  const accent = colour === "orange" ? "#fda500" : "#254391";
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 left-0 h-1 w-16" style={{ background: accent }} />
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-bold">{label}</div>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className={`text-3xl font-bold tracking-tight text-[#0A1128] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
